import { createRef, useEffect } from 'react';
import type { SyntheticEvent } from 'react';

import { css } from '@compiled/react';
// @ts-ignore
import { useInView } from 'react-intersection-observer';

import { media } from '@atlaskit/primitives/responsive';

import { Adg4RaisedMedia } from '@/components/adg4/raised-media/src/raised-media';
import { useAdg4TabsContext } from '@/components/adg4/tabs/src/tabs';
import { wacToken } from '@/libraries/design/foundations';
import { FeatureInfo, FeatureInfoProps } from '@/patterns/feature-info/feature-info';

const imageStyle = css({
	paddingBlockEnd: 24,
	[media.above.md]: {
		paddingBlockStart: 24,
		paddingInlineStart: 24,
	},
});

const reusableImageStyle = css({
	paddingBlock: '4%',
	paddingInline: '5%',
	position: 'relative',
	zIndex: 1,
});

const backgroundStyle = css({
	'&:before': {
		content: '""',
		backgroundColor: wacToken('color.primary.lime'),
		position: 'absolute',
		left: '-1%',
		top: '0',
		width: '102%',
		height: '100%',
		zIndex: -1,
		clipPath: 'polygon(100% 0%, 100% 33%, 55% 33%, 55% 100%, 3% 97%, 2% 33%, 0% 33%, 0% 1%)',
		'@media (max-width: 1175px)': {
			height: '95%',
			insetBlockStart: '2.5%',
		},
		'@media (max-width: 1150px)': {
			height: '92.5%',
			insetBlockStart: '3.75%',
		},
		'@media (max-width: 1125px)': {
			height: '90%',
			insetBlockStart: '5%',
		},
		'@media (max-width: 1100px)': {
			height: '85%',
			insetBlockStart: '7.5%',
		},
		'@media (max-width: 1075px)': {
			height: '80%',
			insetBlockStart: '10%',
		},
		'@media (max-width: 1050px)': {
			height: '75%',
			insetBlockStart: '12.5%',
		},
		[media.below.md]: {
			content: 'none',
		},
	},
	'&:after': {
		content: '""',
		backgroundImage:
			'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-accent-4.webp)',
		position: 'absolute',
		left: '-112px',
		bottom: '-145px',
		width: '274px',
		height: '337px',
		scale: '30%',
		'@media (max-width: 1175px)': {
			scale: '25%',
			insetBlockEnd: '-135px',
		},
		'@media (max-width: 1150px)': {
			insetBlockEnd: '-130px',
		},
		'@media (max-width: 1125px)': {
			insetBlockEnd: '-125px',
		},
		'@media (max-width: 1100px)': {
			insetBlockEnd: '-120px',
		},
		'@media (max-width: 1075px)': {
			scale: '20%',
			insetBlockEnd: '-105px',
		},
		'@media (max-width: 1050px)': {
			insetBlockEnd: '-100px',
		},
		[media.below.md]: {
			content: 'none',
		},
	},
});

const reusuablePurpleBackgroundStyle = css({
	'&:before': {
		content: '""',
		backgroundColor: wacToken('color.primary.purple'),
		position: 'absolute',
		insetInlineStart: '0%',
		insetBlockStart: '0%',
		width: '100%',
		height: '100%',
		zIndex: -1,
		clipPath:
			'polygon(99.9% 77.5%, 96.8% 77.5%, 96.8% 100%, 1.6% 97.4%, 0% 24.9%, 49.7% 21.5%, 55% 1.2%, 99.9% 0%)',
	},
});

const reusuableGreenBackgroundStyle = css({
	'&:before': {
		content: '""',
		backgroundColor: wacToken('color.primary.lime'),
		position: 'absolute',
		insetInlineStart: '0%',
		insetBlockStart: '0%',
		width: '100%',
		height: '100%',
		zIndex: -1,
		'clip-path':
			'polygon(38.3% 2.6%, 74.6% 2.6%, 94.0% 63.8%, 100% 63.8%, 99.1% 100%, 23.4% 98.1%, 21.9% 75.0%, 0.7% 73.7%, 0% 0%, 37.7% 0%, 38.3% 2.6%)',
	},
});

const reusuableOrangeBackgroundStyle = css({
	'&:before': {
		content: '""',
		backgroundColor: wacToken('color.primary.saffron'),
		position: 'absolute',
		insetInlineStart: '1%',
		insetBlockStart: '0%',
		width: '90%',
		height: '100%',
		zIndex: -1,
		'clip-path':
			'polygon(100% 0%, 99.2% 96.5%, 75.7% 95.4%, 75.5% 98.7%, 0% 100%, 0% 11.5%, 35.3% 13.1%, 35.3% 14.9%, 38.3% 0%, 100% 0%)',
	},
});

const videoStyle = css({
	maxWidth: '100%',
});

const combinedVideoSizing = css({
	display: 'flex',
	[media.below.sm]: {
		height: '52vw',
	},
	[media.only.sm]: {
		height: '40vw',
	},
	'&.reusable': {
		[media.below.sm]: {
			height: 'auto',
		},
		[media.only.sm]: {
			height: 'auto',
		},
	},
});

const iconSpacingOverride = css({
	// override the icon and text spacing
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	li: {
		gap: '8px',
		[media.below.lg]: {
			marginInlineEnd: '0px',
		},
	},
	// add gap between each description item
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	ul: {
		gap: '12px',
	},

	// responsive image
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		[media.below.lg]: {
			maxWidth: '100%',
			height: 'auto',
		},
	},
	// cta button padding override
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div > div:last-child > div > div:last-child': {
		[media.below.lg]: {
			paddingInlineStart: '0px',
			paddingInlineEnd: '0px',
		},
	},
});

// eslint-disable-next-line complexity
export const ContentTypeVideo = ({
	active,
	videoSrcMp4,
	videoSrcWebm,
	videoPreviewSrc,
	showBackground,
	useSpecificBackgroundStyle = 'none',
	isUseReusableXflowFeatureInfo = false,
	addPaddingAboveListInReusableXflow = false,
	...props
}: FeatureInfoProps & {
	active?: boolean;
	videoSrcMp4: string;
	videoSrcWebm: string;
	videoPreviewSrc: string;
	showBackground?: boolean;
	useSpecificBackgroundStyle?: 'purple' | 'green' | 'orange' | 'none';
	isUseReusableXflowFeatureInfo?: boolean;
	addPaddingAboveListInReusableXflow?: boolean;
	// eslint-disable-next-line complexity
}) => {
	const { userSelected, goToNextTab } = useAdg4TabsContext();
	const { ref: containerRef, inView: sectionIsVisible } = useInView({
		initialInView: true,
		threshold: 0.3,
	});
	const videoRef = createRef<HTMLVideoElement>();

	useEffect(() => {
		if (sectionIsVisible && active && videoRef.current && videoRef.current.play) {
			videoRef.current.autoplay = true;
			videoRef.current.currentTime = 0;
			videoRef.current.play().catch(() => {
				// Ignore error likely due to autoplay restrictions
			});
		} else if (!active || !sectionIsVisible) {
			if (videoRef.current?.paused) {
				return;
			}

			videoRef.current?.pause();
		}
	}, [active, videoRef, sectionIsVisible]);

	const FeatureInfoImageAndVideo = (
		<div
			ref={containerRef}
			css={[
				isUseReusableXflowFeatureInfo ? reusableImageStyle : imageStyle,
				showBackground && useSpecificBackgroundStyle === 'none' && backgroundStyle,
				showBackground && useSpecificBackgroundStyle === 'purple' && reusuablePurpleBackgroundStyle,
				showBackground && useSpecificBackgroundStyle === 'green' && reusuableGreenBackgroundStyle,
				showBackground && useSpecificBackgroundStyle === 'orange' && reusuableOrangeBackgroundStyle,
			]}
		>
			<Adg4RaisedMedia className={isUseReusableXflowFeatureInfo ? 'reusable-xflow-video' : ''}>
				<div css={combinedVideoSizing} className={isUseReusableXflowFeatureInfo ? 'reusable' : ''}>
					<video
						css={videoStyle}
						playsInline={true}
						muted={true}
						preload="auto"
						poster={videoPreviewSrc}
						autoPlay={!!active}
						loop={userSelected}
						ref={videoRef}
						onEnded={() => {
							if (!userSelected && !isUseReusableXflowFeatureInfo) {
								goToNextTab();
							}
						}}
						// Use onLoadedMetadata to trigger a Safari autplay workaround
						onLoadedMetadata={(videoEvent: SyntheticEvent<HTMLVideoElement, Event>) => {
							if (!active) {
								return;
							}
							try {
								videoEvent.currentTarget.currentTime = 0; // Set currentTime to workaround Safari not autplaying
								if (videoEvent.currentTarget.parentElement) {
									videoEvent.currentTarget.play().catch(() => false); // Call play() as backup to ensure the video is playing
								}
							} catch (error) {
								console.log('Error calling play method on video after onCanPlay is invoked.');
							}
						}}
					>
						<source src={videoSrcMp4} type={`video/mp4`} />
						<source src={videoSrcWebm} type={`video/webm`} />
					</video>
				</div>
			</Adg4RaisedMedia>
		</div>
	);

	return isUseReusableXflowFeatureInfo ? (
		<div css={iconSpacingOverride} className="tab-content-image-container">
			<FeatureInfo {...props} reverseLayout>
				{FeatureInfoImageAndVideo}
			</FeatureInfo>
		</div>
	) : (
		<FeatureInfo {...props} reverseLayout>
			{FeatureInfoImageAndVideo}
		</FeatureInfo>
	);
};
